import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ShopHeroImageBlockConfig } from '@front-platform/content-builder';
import { shopHeroImageBlockResolveConfig } from '@front-platform/content-builder';
import dayjs from 'dayjs';
import { combineLatest } from 'rxjs';
import weekday from 'dayjs/plugin/weekday';
import { map, take, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, inject, input } from '@angular/core';
import { map as rxMap } from 'rxjs';

import { EntityKeys } from '@config/keys/entity.keys';
import { DistributionModeKeys, ShopKeys } from '@config/keys/shop.keys';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { BasketStoreRefiner } from '@common/refiners/basket.refiner';
import { BasketStoreSelector } from '@common/selectors/basket.selector';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';

import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { DeliveryDistributionModes } from '@config/values/order.values';
import { GroupedOrderModalsAttributes } from '@config/modals/grouped-order-modals.config';

import { DoodShopModel } from '@core/models/shop.model';
import { DoodEntityModel } from '@core/models/entity.model';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from 'src/app/core/models/content-builder-fields.model';

import { ShopsService } from '@core/services/shops/shops.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { OrderTypeService } from '@core/services/order-type/order-type.service';
import { ResaNinjaService } from '@core/services/resa-ninja/resa-ninja.service';
import { AuthFirebaseService } from '@core/services/api/auth-firebase/auth-firebase.service';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';

import { AlertModalComponent } from '@shared/modals/alert-modal/alert-modal.component';
import { AuthenticationModalComponent } from '@shared/modals/authentication-modal/authentication-modal.component';
import { CartParametersModalComponent } from '@shared/modals/cart-parameters-modal/cart-parameters-modal.component';
import { ShopInformationsModalComponent } from '@shared/modals/shop-informations-modal/shop-informations-modal.component';
import { ShareGroupedOrderModalComponent } from '@shared/modals/share-grouped-order-modal/share-grouped-order-modal.component';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { shouldShowDesktopHeroImageFallback } from './shop-hero-image-block.visibility';
import { SHOP_HERO_IMAGE_BLOCK_DATA_SOURCE } from './shop-hero-image-block-view-model';
import { provideShopHeroImageBlockDataSource } from './shop-hero-image-block-data-source.providers';
export interface ShopHeroImageBlockRuntimeConfig {}

export type ShopHeroImageBlockConfigInput = Partial<
  Omit<ShopHeroImageBlockConfig, keyof ShopHeroImageBlockRuntimeConfig> &
    ShopHeroImageBlockRuntimeConfig
>;
export type ShopHeroImageBlockResolvedConfig = Omit<
  ShopHeroImageBlockConfig,
  keyof ShopHeroImageBlockRuntimeConfig
> &
  Partial<ShopHeroImageBlockRuntimeConfig>;
export interface ShopHeroImageBlockContract extends ConfigComponentContract<
  ShopHeroImageBlockConfigInput,
  ShopHeroImageBlockResolvedConfig
> {}

@Component({
  selector: 'app-shop-hero-image-block',
  templateUrl: './shop-hero-image-block.component.html',
  standalone: false,
  providers: [...provideShopHeroImageBlockDataSource()],
})
export class ShopHeroImageBlockComponent
  extends DestroyerBase
  implements OnInit, ShopHeroImageBlockContract
{
  private readonly blockDataSource = inject(SHOP_HERO_IMAGE_BLOCK_DATA_SOURCE);

  readonly config = input<ShopHeroImageBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ShopHeroImageBlockConfig,
    ShopHeroImageBlockRuntimeConfig
  >(shopHeroImageBlockResolveConfig, () => ({ config: this.config() }));
  get displayImage(): boolean {
    return this.resolvedConfig()['display_image'] as unknown as boolean;
  }
  get displayAddress(): boolean {
    return this.resolvedConfig()['display_address'] as unknown as boolean;
  }
  get displayCategory(): boolean {
    return this.resolvedConfig()['display_category'] as unknown as boolean;
  }
  get displayMoreInfoButton(): boolean {
    return this.resolvedConfig()['display_more_info_button'] as unknown as boolean;
  }
  get overrideImage(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['override_image'] as unknown as
      IContentBuilderFieldImage | undefined;
  }
  get titleSize(): 'XS' | 'S' | 'M' | 'L' {
    return this.resolvedConfig()['title_size'] as unknown as 'XS' | 'S' | 'M' | 'L';
  }
  get titleFont(): 'accent' | 'base' | 'brand' {
    return this.resolvedConfig()['title_font'] as unknown as 'accent' | 'base' | 'brand';
  }

  get backgroundColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor;
  }
  get shopNameColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['shop_name_color'] as unknown as IContentBuilderFieldColor;
  }
  get textColor(): IContentBuilderFieldColor {
    return this.resolvedConfig()['text_color'] as unknown as IContentBuilderFieldColor;
  }

  selectFirebaseToken$ = this.authSelector.selectStatusToken;

  entity?: DoodEntityModel;
  shop: DoodShopModel | null = null;
  isShopLoading$ = this.blockDataSource.vm$.pipe(rxMap(vm => vm.isShopLoading));

  basketId$ = this.basketSelector.selectId;

  shopKeys = ShopKeys;
  entityKeys = EntityKeys;
  shopImageCover?: string;
  closedLabel?: string;
  isPreorderingAllowed?: boolean;
  isGroupedOrderAllowed?: boolean;
  isBigHeaderImage?: boolean;
  displayBookButton = false;

  isGroupedOrderActive = false;

  get imageSrcUrl(): string | undefined {
    return this.overrideImage?.url ?? this.shopImageCover;
  }

  get showDesktopImageFallback(): boolean {
    return shouldShowDesktopHeroImageFallback({
      shop: this.shop,
      displayImage: this.displayImage,
      imageSrcUrl: this.imageSrcUrl,
    });
  }

  constructor(
    private authSelector: AuthStoreSelector,
    private cartSelector: CartStoreSelector,
    private basketRefiner: BasketStoreRefiner,
    private basketSelector: BasketStoreSelector,
    private readonly shopsService: ShopsService,
    private readonly modalsService: ModalsService,
    private readonly orderTypeService: OrderTypeService,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly resaNinjaService: ResaNinjaService,
    private readonly translateService: TranslateService,
    private readonly authFirebaseService: AuthFirebaseService,
    private readonly navigationHistoryService: NavigationHistoryService,
    private readonly analyticsService: AnalyticsService,
  ) {
    super();
    dayjs.extend(weekday);
  }

  ngOnInit(): void {
    combineLatest([
      this.blockDataSource.vm$,
      this.cartSelector.selectActive$,
      this.marketplaceSelector.selectMarketplace$.pipe(map(marketplace => marketplace.blocks)),
    ])
      .pipe(
        takeUntil(this._destroyerRef),
        map(([vm, cart, marketplaceBlocks]) => {
          this.shop = vm.shop;
          if (!vm.shop) {
            this.shopImageCover = undefined;
            return;
          }

          const shop = vm.shop;
          this.isBigHeaderImage = !!marketplaceBlocks?.find(el => el.type === 'HeaderBigLogoBlock');
          this.shopImageCover = shop?.images?.cover?.url;
          this.isPreorderingAllowed =
            this.orderTypeService.getCapabilities(cart?.type).preorderingAllowed &&
            this.orderTypeService.getCapabilities(cart?.type).useOpenHours &&
            this.shop?.[ShopKeys.Available];

          const distributionModeFound = shop?.[ShopKeys.DistributionModes]?.find(
            distributionMode =>
              DeliveryDistributionModes.includes(distributionMode.type) ||
              distributionMode.type === this.cartSelector.active?.type,
          );
          this.isGroupedOrderAllowed =
            distributionModeFound?.[DistributionModeKeys.AllowGroupedOrder] || false;

          const orderType = this.cartSelector.active?.type;
          this.closedLabel = this.shopsService.getClosedMessage(shop, null, orderType);

          // Legacy image
          if (!this.shopImageCover && shop?.cover) {
            // TODO: Use environment variable instead
            this.shopImageCover = 'https://api.dood.com' + shop?.cover;
          }

          const resaNinjaPlugin = this.resaNinjaService.getPlugin(this.shop);
          if (resaNinjaPlugin) {
            this.displayBookButton = true;
          }
        }),
      )
      .subscribe();

    this.authSelector.selectStatusToken
      .pipe(
        takeUntil(this._destroyerRef),
        map(token => !!token),
      )
      .subscribe(token =>
        token && this.isGroupedOrderActive
          ? this.modalsService.open(
              ShareGroupedOrderModalComponent.handle,
              GroupedOrderModalsAttributes.ShareLink,
            )
          : null,
      );
  }

  goBack(): void {
    this.navigationHistoryService.goBack();
  }

  openShopInformationsModal(): void {
    this.modalsService.open(ShopInformationsModalComponent.handle);
  }

  openGroupOrderModal(): void {
    this.isGroupedOrderActive = true;
    combineLatest([
      this.authFirebaseService.isAuthenticate$().pipe(
        take(1),
        map(v => !!v),
      ),
      this.authSelector.selectStatusToken.pipe(
        take(1),
        map(token => !!token),
      ),
      this.authSelector.selectUserId.pipe(
        take(1),
        map(id => !!id),
      ),
      this.basketRefiner.selectHasBasket,
    ])
      .pipe(
        take(1),
        map(([firebaseUser, firebaseToken, userId, isBasketExist]) => [
          firebaseUser && firebaseToken && userId,
          isBasketExist,
        ]),
        map(([logged, isBasketExist]) => {
          if (logged && isBasketExist) {
            this.modalsService.open(
              ShareGroupedOrderModalComponent.handle,
              GroupedOrderModalsAttributes.GeneratedLink,
            );
          }
          if (logged) {
            this.modalsService.open(
              ShareGroupedOrderModalComponent.handle,
              GroupedOrderModalsAttributes.ShareLink,
            );
          } else {
            this.modalsService.open(AuthenticationModalComponent.handle);
          }
        }),
      )
      .subscribe();
  }

  onOpenCartParameters(): void {
    this.modalsService.open(CartParametersModalComponent.handle);
  }

  openBookUrl(): void {
    const resaNinjaPlugin = this.resaNinjaService.getPlugin(this.shop);
    if (!resaNinjaPlugin) {
      return;
    }

    const bookUrl = this.resaNinjaService.getBookUrl(this.shop);

    if (bookUrl) {
      window.location.href = bookUrl;
    } else {
      this.modalsService.open(AlertModalComponent.handle);
      this.modalsService.setData(AlertModalComponent.handle, {
        message: this.translateService.instant('shop-page.service-not-available'),
      });
    }
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ShopHeroImageBlockComponent, { 'keyAliases': {} });
